import { debug } from "utils";
import { lazy } from "react";

const STORAGE_KEY_PREFIX = "csa-chunk-force-refreshed:";

/**
 * Each lazy-loaded chunk needs its own "have I already retried" flag.
 * A single shared flag would let one chunk's successful load reset another
 * chunk's retry state, so a chunk that keeps failing (e.g. a missing asset)
 * would never see `true` on its second attempt and would reload forever.
 *
 * After bundling, `componentImport.toString()` embeds the resolved chunk's
 * path/hash, which makes it a stable, per-chunk key without requiring every
 * call site to supply one explicitly.
 */
const getStorageKey = (componentImport) => `${STORAGE_KEY_PREFIX}${componentImport.toString()}`;

const getChunkHasForceRefreshed = (storageKey) => {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey) || "false");
  } catch (_e) {
    // since we failed to find a value it is impossible to know if we've already retried.
    // we'll return `true` to act as though we've already retried - this avoids the potential of infinitely retrying.
    return true;
  }
};

const setChunkHasForceRefreshed = (storageKey, val) => {
  try {
    // Clean up rather than leaving a stale `false`/`true` entry behind -
    // this key only needs to exist for the brief window between a failed
    // load and the reload it triggers.
    if (val) {
      window.localStorage.setItem(storageKey, JSON.stringify(val));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // nothing to do here as a setter returns void anyway
  }
};

/**
 * Wrapper over react `lazy` which will reload the current page if a chunk fails to load.
 * The rationale is that if a chunk fails to load there is a good chance the user is
 * currently running an older version of the application.
 * A reload will fetch the latest version of the app and hopefully fix the failed chunk.
 *
 * Lightly modified from:
 * https://raphael-leger.medium.com/react-webpack-chunkloaderror-loading-chunk-x-failed-ac385bd110e0
 */
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const storageKey = getStorageKey(componentImport);
    const chunkHasAlreadyBeenForceRefreshed = getChunkHasForceRefreshed(storageKey);

    try {
      const component = await componentImport();
      setChunkHasForceRefreshed(storageKey, false);
      return component;
    } catch (error) {
      if (chunkHasAlreadyBeenForceRefreshed) {
        debug.warn("Failed to lazy load a chunk for the second time.");
        // We've hit an error loading a chunk and have already tried to reload the page to fix it.
        // We'll now let the error propagate so that our error boundary and BugSnag are triggered.
        setChunkHasForceRefreshed(storageKey, false);
        throw error;
      }

      debug.warn("Failed to lazy load a chunk for the first time. Reloading the page.");
      // We've hit an error loading a JS Chunk.
      // One likely cause is that a newer version of the app has been deployed.
      // We'll attempt to reload the page (just once) to see if getting the latest version of the app fixes the error.
      setChunkHasForceRefreshed(storageKey, true);
      return window.location.reload();
    }
  });

export default lazyWithRetry;
